import { Link } from "react-router-dom";
import { CalendarBlank } from "@phosphor-icons/react";
import { FAMILY } from "@/lib/games";
import { formatDate, formatTime24to12, cycleDateIST } from "@/lib/format";
import { prefetch } from "@/lib/apiCache";

/**
 * Result circle: outlined circle in the family colour with the number inside.
 * Shows "XX" placeholder when the result for the current cycle isn't published yet.
 */
function ResultCircle({ value, borderCls }) {
  const shown = value || "XX";
  const dim = !value;
  return (
    <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full border-[3px] ${borderCls} flex items-center justify-center`}>
      <span className={`font-numbers text-[34px] sm:text-[38px] leading-none text-black ${dim ? "opacity-70" : ""}`}>
        {shown}
      </span>
    </div>
  );
}

export default function GameCard({ game, result }) {
  const f = FAMILY[game.color_family] || FAMILY.night;
  const expected = cycleDateIST(game);
  const hasCurrent = result && result.result_date === expected;

  // If today's not published yet, show whatever we have (previous date + previous FR/SR).
  const frValue = result?.fr || null;
  const srValue = result?.sr || null;
  const awaiting = !hasCurrent;
  const shownDate = result?.result_date || expected;
  const displayDate = formatDate(shownDate, { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div
      className={`bg-white rounded-2xl overflow-hidden border ${f.border} shadow-[0_10px_30px_-14px_rgba(0,0,0,0.25)] flex flex-col`}
      data-testid={`game-card-${game.slug}`}
    >
      {/* colored top strip — game name + Live Board pill */}
      <div className={`${f.head} px-3 py-2.5 flex items-center justify-between gap-2`}>
        <div className="font-numbers text-[20px] sm:text-[22px] leading-tight line-clamp-2 uppercase">
          {game.name}
        </div>
        <div className="inline-flex items-center gap-1.5 bg-white text-black rounded-full px-2.5 py-1 text-[11px] font-numbers shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
          Live Board
        </div>
      </div>

      {/* body */}
      <div className="px-3 sm:px-4 py-4">
        {/* date */}
        <div className="flex items-center justify-center gap-2 text-black">
          <CalendarBlank size={18} weight="fill" />
          <span className="font-numbers text-[15px] sm:text-[16px]">{displayDate}</span>
        </div>

        {/* two columns */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          {/* FR */}
          <div className="text-center flex flex-col items-center">
            <div className="font-numbers text-[14px] sm:text-[15px] text-black">
              F/R <span className="text-neutral-400">({formatTime24to12(game.fr_time)})</span>
            </div>
            <div className="mt-3" data-testid={`fr-${game.slug}`}>
              <ResultCircle value={frValue} borderCls={f.border} />
            </div>
          </div>
          {/* SR */}
          <div className="text-center flex flex-col items-center border-l border-neutral-100">
            <div className="font-numbers text-[14px] sm:text-[15px] text-black">
              S/R <span className="text-neutral-400">({formatTime24to12(game.sr_time)})</span>
            </div>
            <div className="mt-3" data-testid={`sr-${game.slug}`}>
              <ResultCircle value={srValue} borderCls={f.border} />
            </div>
          </div>
        </div>

        {awaiting && (
          <div className="font-numbers mt-3 text-center text-[11px] sm:text-[12px] uppercase tracking-widest text-neutral-500">
            Waiting Result
          </div>
        )}
      </div>

      {/* pill-shaped bottom buttons */}
      <div className="grid grid-cols-2 gap-3 px-3 sm:px-4 pb-4">
        <Link
          to={`/${game.slug}/previous-result`}
          onMouseEnter={() => prefetch(`/results/${game.slug}?limit=500`)}
          onTouchStart={() => prefetch(`/results/${game.slug}?limit=500`)}
          className="font-numbers rounded-full border-2 border-black text-blue-700 text-center py-2 text-[15px] sm:text-[16px] hover:bg-blue-50 active:scale-[0.98] transition"
          data-testid={`previous-${game.slug}`}
        >
          Previous
        </Link>
        <Link
          to={`/${game.slug}/common-number`}
          onMouseEnter={() => prefetch(`/results/${game.slug}?limit=10`)}
          onTouchStart={() => prefetch(`/results/${game.slug}?limit=10`)}
          className="font-numbers rounded-full border-2 border-black text-blue-700 text-center py-2 text-[15px] sm:text-[16px] hover:bg-blue-50 active:scale-[0.98] transition"
          data-testid={`common-${game.slug}`}
        >
          Common
        </Link>
      </div>
    </div>
  );
}
